import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Block } from '@awarego-ui/components';
import TableServer from '../../../../components/table/TableServer';
import DateCell from '../../../../components/manager/DateCell';
import { useStores } from '../../../../hooks/use-stores';
import { TABLE_DEFAULT_PAGE_SIZE } from '../../../../utils/constants';

function ScormTrainingEmployees({ companyId, training }) {
    const { automationStore } = useStores();
    // training.id is the numeric package id; the users endpoint routes SCORM by the `p` prefix.
    const automationId = `p${training.id}`;
    // SCORM has no "sent/unsent" subjects — subjectsSent is always the full subject count.
    const subjectsSent = training._rawData?.subjectsSent || 1;

    const [query, setQuery] = useState({
        p: 0,
        n: TABLE_DEFAULT_PAGE_SIZE,
        order: 'email asc',
        filter: '',
    });

    useEffect(() => {
        if (!companyId || !training.id) return;
        automationStore.loadScormUsers(companyId, automationId, query);
    }, [automationStore, companyId, training.id, automationId, query]);

    const onChangePagingParams = useCallback((p, n) => {
        setQuery((current) =>
            current.p === p && current.n === n ? current : { ...current, p, n }
        );
    }, []);

    const onChangeSort = useCallback((sortBy) => {
        if (!sortBy || sortBy.length === 0) return;
        const order = `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}`;
        setQuery((current) =>
            current.order === order ? current : { ...current, order }
        );
    }, []);

    const columns = useMemo(
        () => [
            { Header: 'Email', accessor: 'email' },
            {
                Header: 'Name',
                accessor: 'learnerName',
                Cell: (x) => x.value || '—',
            },
            { Header: 'Subjects completed', accessor: 'subjectsDone' },
            {
                Header: 'Last activity',
                accessor: 'lastActivity',
                Cell: (x) => <DateCell date={x.value} />,
            },
            {
                Header: 'Score',
                accessor: 'score',
                Cell: (x) =>
                    `${Math.round((x.value * 100) / subjectsSent)}%`,
            },
        ],
        [subjectsSent]
    );

    const usersData = automationStore.scormUsers.slice();
    const usersTotal = automationStore.scormUsersTotal;

    return (
        <Block tablePadding noBorder resetMargin>
            <TableServer
                columns={columns}
                data={usersData}
                rowsTotal={usersTotal}
                pagesTotal={Math.ceil(usersTotal / query.n)}
                initialPageSize={query.n}
                defaultSortBy="email"
                defaultSortDescent={false}
                hidePagination={usersTotal <= TABLE_DEFAULT_PAGE_SIZE}
                onChangePagingParams={onChangePagingParams}
                onChangeSort={onChangeSort}
                loadingData={automationStore.loadingScormUsers}
                manualInitialPageTrigger={query.filter}
            />
        </Block>
    );
}

export default observer(ScormTrainingEmployees);
